/**
 * @format
 */
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { PlanValidationResult, ValidatablePlan } from 'common-typescript';
import {
    CourseUnit,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    CustomStudyDraft,
    EntityWithRule,
    Module,
} from 'common-typescript/types';
import { compact, filter, flatMap, get, includes, isEmpty, keys, map, some, values } from 'lodash-es';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from 'sis-components/plan/plan-actions-service/plan-actions.service';
import { PlanData, PlanStateObject } from 'sis-components/service/plan-state.service';

@Component({
    selector: 'app-plan-structure-edit-invalid-selections',
    templateUrl: './plan-structure-edit-invalid-selections.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class PlanStructureEditInvalidSelectionsComponent implements OnInit {
    planActionsService: PlanActionsService = inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN);

    hasInvalidSelections = false;

    @Input() parentModule: EntityWithRule;
    @Input() planData: PlanData;
    @Input() planStateObject: PlanStateObject;
    @Input() planValidationResult: PlanValidationResult;
    @Input() validatablePlan: ValidatablePlan;
    @Input() headingLevel: number;

    invalidCourseUnits: CourseUnit[];
    invalidCustomCourseUnitAttainments: CustomCourseUnitAttainment[];
    invalidModules: Module[];
    invalidCustomModuleAttainments: CustomModuleAttainment[];
    invalidCustomStudyDrafts: CustomStudyDraft[];

    ngOnInit() {
        this.invalidCourseUnits = filter(
            this.validatablePlan.getSelectedCourseUnitsUnderModule(this.parentModule),
            (courseUnit: CourseUnit) => get(this.planStateObject.courseUnits, [courseUnit.id, 'invalidSelection']) === true,
        );
        this.invalidCustomCourseUnitAttainments = filter(
            this.validatablePlan.getSelectedCustomCourseUnitAttainmentsUnderModule(this.parentModule),
            (ccua: CustomCourseUnitAttainment) =>
                get(this.planStateObject.customCourseUnitAttainments, [ccua.id, 'invalidSelection']) === true,
        );
        this.invalidModules = filter(
            this.validatablePlan.getSelectedModulesUnderModule(this.parentModule),
            (module: Module) => get(this.planStateObject.modules, [module.id, 'invalidSelection']) === true,
        );
        this.invalidCustomModuleAttainments = filter(
            this.validatablePlan.getSelectedCustomModuleAttainmentsUnderModule(this.parentModule),
            (cma: CustomModuleAttainment) => get(this.planStateObject.customModuleAttainments, [cma.id, 'invalidSelection']) === true,
        );
        this.invalidCustomStudyDrafts = this.getInvalidCustomStudyDrafts();

        const allInvalidStudyArrays = [
            this.invalidCourseUnits,
            this.invalidCustomCourseUnitAttainments,
            this.invalidModules,
            this.invalidCustomModuleAttainments,
            this.invalidCustomStudyDrafts,
        ];

        if (some(allInvalidStudyArrays, array => !isEmpty(array))) {
            this.hasInvalidSelections = true;
        }
    }

    handleCourseUnitToggle(courseUnit: CourseUnit) {
        if (this.validatablePlan.isCourseUnitInPlan(courseUnit)) {
            this.planActionsService?.removeCourseUnit(courseUnit, this.parentModule);
        } else {
            this.planActionsService?.forceSelectCourseUnitById(courseUnit.id, this.parentModule);
        }
    }

    handleCustomCourseUnitAttainmentToggle(customCourseUnitAttainment: CustomCourseUnitAttainment) {
        if (this.validatablePlan.isCustomCourseUnitAttainmentInPlan(customCourseUnitAttainment)) {
            this.planActionsService?.removeCustomCourseUnitAttainmentById(customCourseUnitAttainment.id, this.parentModule);
        } else {
            this.planActionsService?.selectCustomCourseUnitAttainment(customCourseUnitAttainment, this.parentModule);
        }
    }

    handleCustomStudyDraftToggle(customStudyDraft: CustomStudyDraft) {
        if (this.isCustomStudyDraftInPlan(customStudyDraft)) {
            this.planActionsService?.removeCustomStudyDraft(customStudyDraft, this.parentModule);
        } else {
            this.planActionsService?.addCustomStudyDraft(customStudyDraft, this.parentModule);
        }
    }

    handleModuleToggle(module: Module) {
        if (this.validatablePlan.isModuleInPlan(module.id)) {
            this.planActionsService?.removeModule(module, this.parentModule);
        } else {
            this.planActionsService?.forceSelectModuleById(module.id, this.parentModule);
        }
    }

    handleCustomModuleAttainmentToggle(customModuleAttainment: CustomModuleAttainment) {
        if (this.validatablePlan.isModuleAttainmentInPlan(customModuleAttainment.id)) {
            this.planActionsService?.removeCustomModuleAttainmentById(customModuleAttainment.id, this.parentModule);
        } else {
            this.planActionsService?.selectCustomModuleAttainment(customModuleAttainment, this.parentModule);
        }
    }

    isCustomStudyDraftInPlan(customStudyDraft: CustomStudyDraft) {
        return includes(
            map(this.validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(this.parentModule.id), 'id'),
            customStudyDraft.id,
        );
    }

    getInvalidCustomStudyDrafts() {
        const ruleValidationResultsForModule = values(get(this.planValidationResult?.ruleValidationResults, this.parentModule.id));
        const matchedCustomStudyDraftIds = flatMap(compact(flatMap(ruleValidationResultsForModule, 'selectedCustomStudyDraftsById')), keys);
        const unmatchedCustomStudyDrafts = filter(
            this.validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(this.parentModule.id),
            csd => !includes(matchedCustomStudyDraftIds, csd.id),
        );
        return unmatchedCustomStudyDrafts;
    }
}
